import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { LOCALES, type Locale } from '@diana/shared'
import { api, type TeacherLesson } from '../../lib/api.js'
import { LocaleScope } from '../../i18n/index.js'
import { ErrorLine, More, Skeleton } from '../../classroom/ui.js'
import { Frame, Player } from '../student/player/LessonPlayer.js'
import { playerCopy } from '../student/player/copy.js'

/**
 * The teacher view (Studio → client card → plan → „Prowadź lekcję”): the
 * lesson exactly as the student walks it, homework included, with the
 * answer key and the chosen student's answers. Diana hides the key with one
 * click — or „K” — before she shares her screen.
 *
 * Everything that changes the view lives in the URL, so a reload keeps it:
 * `uczen` (client card), `plan` (whose members the switch offers), `klucz`
 * („ukryty” when hidden), `jezyk` (the explanations' language; the
 * student's own by default) and the player's `krok`.
 */
export function TeacherLessonPage() {
  const { slug = '', lessonSlug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const clientId = params.get('uczen')
  const planId = params.get('plan')
  const keyShown = params.get('klucz') !== 'ukryty'

  const lesson = useQuery({
    queryKey: ['studio', 'teach', slug, lessonSlug, clientId],
    queryFn: () => api.studio.teacherLesson(slug, lessonSlug, clientId),
    // Switching student keeps the lesson on screen while their answers load.
    placeholderData: keepPreviousData,
  })
  const plan = useQuery({ queryKey: ['studio', 'plan', planId], queryFn: () => api.studio.plan(planId!), enabled: Boolean(planId) })

  const asked = params.get('jezyk')
  const wanted = asked ?? lesson.data?.student?.locale ?? 'pl'
  const locale: Locale = (LOCALES as readonly string[]).includes(wanted) ? (wanted as Locale) : 'pl'
  const t = playerCopy(locale)

  const set = (name: string, value: string | null) =>
    setParams(
      (p) => {
        const q = new URLSearchParams(p)
        if (value === null) q.delete(name)
        else q.set(name, value)
        return q
      },
      { replace: true },
    )

  // „K” flips the key — quicker than finding the button with a screen being shared.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'k' && e.key !== 'K') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if ((e.target as HTMLElement | null)?.closest('input, textarea, select, [contenteditable]')) return
      set('klucz', keyShown ? 'ukryty' : null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyShown])

  const exitTo = clientId ? `/studio/klienci/${clientId}` : '/studio/kursy'

  if (!lesson.data) {
    return (
      <Frame t={t} exitTo={exitTo} title="" sub="" progress={0}>
        <div className="mx-auto w-full max-w-[660px] px-5 py-10 desk:px-12">
          {lesson.isError ? <ErrorLine>Nie udało się otworzyć lekcji.</ErrorLine> : <Skeleton lines={5} />}
        </div>
      </Frame>
    )
  }

  const data = lesson.data
  const student = data.student
  const members = plan.data?.members ?? (student ? [student] : [])
  // Only somebody with a login can have answers to show.
  const shownStudent = student?.hasLogin ? student.name.split(' ')[0] ?? student.name : null

  const tools = (
    <>
      {members.length > 0 && (
        <select
          aria-label="Odpowiedzi ucznia"
          value={clientId ?? ''}
          onChange={(e) => set('uczen', e.target.value || null)}
          className="cl-input hidden !min-h-10 !w-auto !py-1.5 text-class-gloss desk:block"
        >
          <option value="">Bez ucznia</option>
          {members.map((m) => (
            <option key={m.clientId} value={m.clientId}>
              {m.name}
              {m.hasLogin ? '' : ' (bez konta)'}
            </option>
          ))}
        </select>
      )}
      <select
        aria-label="Język wyjaśnień"
        value={locale}
        onChange={(e) => set('jezyk', e.target.value)}
        className="cl-input hidden !min-h-10 !w-auto !py-1.5 text-class-gloss desk:block"
      >
        <option value="pl">PL</option>
        <option value="uk">UK</option>
        <option value="ru">RU</option>
      </select>
      <button
        type="button"
        aria-pressed={keyShown}
        title="Klawisz K"
        onClick={() => set('klucz', keyShown ? 'ukryty' : null)}
        className={[
          'cl-btn min-h-10 px-4 text-class-gloss',
          keyShown ? 'bg-class-gold-100 text-class-gold-text shadow-[inset_0_0_0_1px_var(--color-class-gold-line)]' : 'cl-btn-secondary',
        ].join(' ')}
      >
        {keyShown ? 'Klucz widoczny' : 'Klucz ukryty'}
      </button>
    </>
  )

  return (
    <LocaleScope locale={locale}>
      <Player
        key={data.id}
        lesson={data}
        slug={slug}
        t={t}
        teacher={{
          state: { keyShown, student: shownStudent },
          tools,
          exitTo,
          finish: <TeacherFinish lesson={data} slug={slug} params={params} exitTo={exitTo} />,
        }}
      />
    </LocaleScope>
  )
}

function TeacherFinish({ lesson, slug, params, exitTo }: { lesson: TeacherLesson; slug: string; params: URLSearchParams; exitTo: string }) {
  const q = new URLSearchParams(params)
  q.delete('krok')
  const search = q.toString() ? `?${q.toString()}` : ''
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <span className="text-class-label font-semibold text-class-ink-faint">{lesson.course.title}</span>
        <h1 className="cl-title m-0">Koniec lekcji {lesson.number}</h1>
        {lesson.student && <p className="m-0 text-class-ink-soft">{lesson.student.name}</p>}
      </div>
      <div className="flex flex-col gap-3">
        {lesson.next && (
          <More to={`/studio/lekcja/${slug}/${lesson.next.slug}${search}`} className="!whitespace-normal">
            Następna lekcja: {lesson.next.number} · {lesson.next.title}
          </More>
        )}
        <More to={exitTo} quiet>
          {lesson.student ? 'Wróć do karty ucznia' : 'Wróć do Studia'}
        </More>
      </div>
    </div>
  )
}
